/**
 * What counts as an acceptable password.
 *
 * Mirrors backend/services/password_policy.py. Shared so every place that sets
 * one — creating a company, adding an admin, adding a team member, changing
 * someone's password — refuses the same things, rather than each form
 * inventing its own minimum.
 *
 * These run in the browser for immediate feedback. They are not the guard: the
 * server checks the same rules, because a form is only a suggestion.
 */

export const MIN_LEN = 8;
export const MAX_LEN = 128;

const HAS_LETTER = /[A-Za-z]/;
const HAS_DIGIT = /\d/;
const HAS_SYMBOL = /[^A-Za-z0-9]/;

// Not a security control on its own — anything here already fails the character
// rules. It exists to say something more useful than "needs a digit" when
// someone reaches for the obvious.
const OBVIOUS = new Set([
  'password', 'password1', 'password@1', 'passw0rd',
  'qwerty', 'qwerty123', 'welcome1', 'letmein',
  'admin123', 'administrator', 'changeme', 'secret',
  '12345678', '123456789', '1234567890',
]);

/** The first thing wrong with a password, or null if it is fine. */
export function passwordError(value) {
  const v = String(value ?? '');
  if (!v) return 'Password is required';
  if (v !== v.trim()) return 'Password cannot start or end with a space';
  if (v.length < MIN_LEN) return `Password must be at least ${MIN_LEN} characters`;
  if (v.length > MAX_LEN) return `Password is too long (maximum ${MAX_LEN} characters)`;
  if (!HAS_LETTER.test(v)) return 'Password must include a letter';
  if (!HAS_DIGIT.test(v)) return 'Password must include a number';
  if (!HAS_SYMBOL.test(v)) return 'Password must include a symbol, like ! @ # or -';
  if (OBVIOUS.has(v.toLowerCase())) return 'That password is too easy to guess — please choose another';
  return null;
}

/**
 * The rules as a checklist, so someone typing can see what is still missing
 * instead of being told one thing at a time.
 */
export function passwordChecks(value) {
  const v = String(value ?? '');
  return [
    { label: `At least ${MIN_LEN} characters`, ok: v.length >= MIN_LEN },
    { label: 'A letter', ok: HAS_LETTER.test(v) },
    { label: 'A number', ok: HAS_DIGIT.test(v) },
    { label: 'A symbol', ok: HAS_SYMBOL.test(v) },
  ];
}
